<template>
  <div class="browse-goals-results-container">
    <p role="status" class="sr-only">Found {{ totalResults }} results.</p>
    <div v-if="loadingResults" class="text-center text-muted">
      <i class="fa fa-circle-o-notch fa-spin fa-fw"/> Loading...
    </div>
    <template v-if="!loadingResults && isAuthenticated && !isTrial">
      <p class="text-muted found-goals-text">Found {{ goals.length === 0 || totalResults <= goals.length ? '' : 'more than ' }}{{ goals.length }} results</p>
      <template v-if="groupBySubject">
        <div v-for="subject in allResultsGrouped.orderedSubjects" :key="subject">
          <h2 v-if="filters.subject && filters.subject !== subject" class="results-group-heading">
            Related content in <a role="button" @click="navigateToRelatedContent(filters.subject, subject)">{{ subject }}</a></h2>
          <div class="row results-group">
            <div
              class="col-md-4"
              v-for="(goal, index) in allResultsGrouped.resultsBySubject[subject]"
              :key="goal.uuid">
              <GoalCardContainer
                :position="index"
                :goal="goal"
                :restriction-type="goal.restrictionType"
                :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
                :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
            </div>
          </div>
        </div>
      </template>
      <div class="row" v-else>
        <div
          class="col-md-4"
          v-for="(goal, index) in sortedGoals"
          :key="goal.uuid">
          <GoalCardContainer
            :position="index"
            :goal="goal"
            :restriction-type="goal.restrictionType"
            :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
            :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
        </div>
      </div>
    </template>

    <template v-else-if="!loadingResults">
      <template v-if="trialResults.length > 0">
        <h2 ref="trialResultsHeader">Available with Trial</h2>
        <p class="text-muted found-goals-text" aria-live="polite">
          Found {{ trialResults.length }} results
          <template v-if="trialResults.length < goals.length">
            (<a href="#premium-results" @click.prevent="scrollToRef($refs.premiumResultsHeader)">
              {{ goals.length < totalResults ? 'Over ' : '' }}{{ premiumResults.length }} more with premium
            </a>)
          </template>
        </p>

        <template v-if="groupBySubject">
          <div v-for="subject in trialResultsGrouped.orderedSubjects" :key="subject">
            <h3 v-if="filters.subject && filters.subject !== subject" class="results-group-heading">
              Related content in <a role="button" @click="navigateToRelatedContent(filters.subject, subject)">{{ subject }}</a></h3>
            <div class="row results-group">
              <div
                class="col-md-4"
                v-for="(goal, index) in trialResultsGrouped.resultsBySubject[subject]"
                :key="goal.uuid">
                <GoalCardContainer
                  :position="index"
                  :goal="goal"
                  :restriction-type="goal.restrictionType"
                  :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
                  :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
              </div>
            </div>
          </div>
        </template>
        <div class="row" v-else>
          <div
            class="col-md-4"
            v-for="(goal, index) in trialResults"
            :key="goal.uuid">
            <GoalCardContainer
              :position="index"
              :goal="goal"
              :restriction-type="goal.restrictionType"
              :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
              :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
          </div>
        </div>
      </template>

      <template v-if="premiumResults.length > 0">
        <h2 id="premium-results" ref="premiumResultsHeader">Premium Content</h2>
        <p class="text-muted found-goals-text" aria-live="polite">
          Found {{ totalResults > goals.length && 'more than ' || '' }}{{ premiumResults.length }} results
          <template v-if="trialResults.length > 0">
            (<a role="button" @click="scrollToRef($refs.trialResultsHeader)">back to trial results</a>)
          </template>
        </p>

        <template v-if="groupBySubject">
          <div v-for="subject in premiumResultsGrouped.orderedSubjects" :key="`premium-${subject}`">
            <h3 v-if="filters.subject && filters.subject !== subject" class="results-group-heading">
              Related content in <a role="button" @click="navigateToRelatedContent(filters.subject, subject)">{{ subject }}</a></h3>
            <div class="row results-group">
              <div
                class="col-md-4"
                v-for="(goal, index) in premiumResultsGrouped.resultsBySubject[subject]"
                :key="goal.uuid">
                <GoalCardContainer
                  :position="index"
                  :goal="goal"
                  :restriction-type="goal.restrictionType"
                  :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
                  :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
              </div>
            </div>
          </div>
        </template>
        <div class="row" v-else>
          <div
            class="col-md-4"
            v-for="(goal, index) in premiumResults"
            :key="goal.uuid">
            <GoalCardContainer
              :position="index"
              :goal="goal"
              :restriction-type="goal.restrictionType"
              :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
              :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { isGoalPremium } from '@/util/goal_scores'
import GoalCardContainer from '@/components/search/goalcard/GoalCardContainer'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'
import { PremiumLocksMixin } from '@/contentprofile/premium_locks_mixin'

export default {
  name: 'BrowseGoalsResults',
  components: {
    GoalCardContainer
  },
  mixins: [noSelMixin, PremiumLocksMixin],
  props: {
    goals: {
      type: Array,
      default() {
        return []
      }
    },
    totalResults: {
      type: Number,
      required: true
    },
    subjectSelected: {
      type: Boolean,
      required: true
    },
    keywordSelected: {
      type: Boolean,
      default: false
    },
    groupBySubject: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {}
  },
  updated() {
    if (!this.loadingResults) {
      this.$sendEvent('Returned Browse Goal Results', {
        browse_goals_num_results: this.goals.length,
        algolia_query_id: this.algoliaQueryID
      })
    }
  },
  computed: {
    sortedGoals() {
      return this.sortGoals(this.goals)
    },
    allResultsGrouped() {
      return this.groupGoals(this.sortedGoals)
    },
    trialResults() {
      if (!this.isAuthenticated || this.isTrial) {
        return this.sortedGoals.filter(goal => !isGoalPremium(goal) && !this.isGoalLocked(goal))
      }
      return []
    },
    trialResultsGrouped() {
      return this.groupGoals(this.trialResults)
    },
    premiumResults() {
      if (!this.isAuthenticated || this.isTrial) {
        return this.sortedGoals.reduce((filteredGoals, goal) => {
          if (isGoalPremium(goal) || this.isGoalLocked(goal)) {
            goal.restrictionType = 'premium'
            return [...filteredGoals, goal]
          }
          return filteredGoals
        }, [])
      }
      return []
    },
    premiumResultsGrouped() {
      return this.groupGoals(this.premiumResults)
    },
    ...mapGetters({
      loadingResults: 'browse/loadingResults',
      filters: 'browse/filters',
      dropdownStandardSetForSubject: 'contentProfile/dropdownStandardSetForSubject',
      standardSetLabelForSubjectCard: 'contentProfile/standardSetLabelForSubjectCard',
      contentProfileSubjects: 'contentProfile/publishedSubjects'
    }),
    ...mapState('browse', ['algoliaQueryID']),
    ...mapGetters('user', ['isTrial', 'isAuthenticated']),
    profileSubjectsList() {
      return this.contentProfileSubjects.map(subjectObj => subjectObj.subject)
    }
  },
  methods: {
    sortGoals(list) {
      // don't mess with re-ordering results if keyword search is specified
      if (this.keywordSelected || list.length < 1) {
        return list
      }

      const newList = list.slice()
      const ishighestGradeLowestGradePresent = Number.isInteger(list[0].highest_grade)

      newList.sort((g1, g2) => {
        if (g2.super_page && !g1.super_page) {
          return 1
        }
        if (!g2.super_page && g1.super_page) {
          return -1
        }
        if (g2.access_point_level && !g1.access_point_level) {
          return 1
        }
        if (!g2.access_point_level && g1.access_point_level) {
          return -1
        }
        if (ishighestGradeLowestGradePresent) {
          // sort according to high/low grades in algolia
          if (g2.highest_grade > g1.highest_grade) {
            return 1
          }
          if (g2.highest_grade < g1.highest_grade) {
            return -1
          }
          if (g2.lowest_grade > g1.lowest_grade) {
            return 1
          }
          if (g2.lowest_grade < g1.lowest_grade) {
            return -1
          }
        } else {
          // sort according to the grades array
          // last grade in array is always highest
          let g1_grade = g1.grades[g1.grades.length - 1]
          let g2_grade = g2.grades[g2.grades.length - 1]
          g1_grade = formatGrade(g1_grade)
          g2_grade = formatGrade(g2_grade)
          if (g2_grade < g1_grade) {
            return -1
          }
          if (g2_grade > g1_grade) {
            return 1
          }
        }
        if (this.subjectSelected) {
          if (g2.score < g1.score) {
            return -1
          }
          if (g2.score > g1.score) {
            return 1
          }
        }
        return 0
      })
      return newList
    },
    groupGoals(list) {
      const orderedSubjects = []
      const resultsBySubject = {}

      list.forEach(goal => {
        let primarySubject;
        if (!this.profileSubjectsList.includes(goal.primary_subject) && this.profileSubjectsList.includes(goal.subject)) {
          primarySubject = goal.subject
        } else {
          primarySubject = goal.primary_subject
        }
        if (!orderedSubjects.includes(primarySubject)) {
          if (this.filters.subject && this.filters.subject === primarySubject) {
            orderedSubjects.unshift(primarySubject)
          } else {
            orderedSubjects.push(primarySubject)
          }
        }
        if (!resultsBySubject[primarySubject]) {
          resultsBySubject[primarySubject] = []
        }
        resultsBySubject[primarySubject].push(goal)
      })

      return {
        orderedSubjects,
        resultsBySubject
      }
    },
    scrollToRef(ref) {
      ref.scrollIntoView({
        behavior: 'smooth'
      })
    },
    navigateToRelatedContent(filterSubject, relatedSubject) {
      this.$sendEvent('Clicked Browse Results Related Subject', {
        browse_goals_filter_subject: filterSubject,
        browse_goals_results_related_subject: relatedSubject
      })
      const query = { subject: relatedSubject }
      if (this.filters.grades) {
        query.grades = this.filters.grades
      }
      this.$router.replace({name: 'BrowseGoalsPage', query})
    }
  }
}

const formatGrade = grade => {
  if (grade === 'K') {
    return 0
  } else if (grade === 'Pre-K') {
    return -1
  } else if (grade === 'Birth-3') {
    return -2
  }
  return parseInt(grade)
}
</script>

<style scoped lang="less">
@gutterWidth: 4px;
.row {
  margin-left: -@gutterWidth;
  margin-right: -@gutterWidth;
}
.results-group {
  margin-bottom: 24px;
}
div.col-md-4 {
  padding-left: @gutterWidth;
  padding-right: @gutterWidth;
  margin-bottom: var(--spacing-xl);
}

h2 {
  font-size: 24px;
  margin-top: 24px;
  margin-bottom: 4px;
}

h3 {
  font-size: 21px;
  margin-top: 24px;
  margin-bottom: 4px;
  color: #666;
}

.results-group-heading {
  margin-top: 4px;
  margin-bottom: 24px;
}

.found-goals-text {
  font-size: 0.9em;
  margin-bottom: 24px;
}
</style>
